import { isPossiblePhoneNumber } from "libphonenumber-js";
import { validatePostalCode } from "app/utils/validate";

export const FACILITY_NAME_MAX_LENGTH = 100;
export const ADDRESS_MAX_LENGTH = 200;
export const CITY_MAX_LENGTH = 60;
export const POSTAL_CODE_MAX_LENGTH = 12;
// RFC 5321's maximum forward-path length.
export const EMAIL_MAX_LENGTH = 254;
// Per name part, not the whole name.
export const NAME_MAX_LENGTH = 50;
export const SESSION_NAME_MAX_LENGTH = 80;
// "99999999.99" is 11 characters, which is far past any real session rate.
export const RATE_MAX_LENGTH = 11;

const CITY_MIN_LENGTH = 2;

// Must start and end alphanumeric, so a lone separator or a trailing "-" is
// caught, with spaces and hyphens allowed in between: "A9A 9A9" (CA),
// "90210-1234" (US), "1234 AB" (NL) all pass. Length is capped by the field's
// maxLength, which also truncates pastes, so there is no upper bound here.
const LOOSE_POSTAL_CODE = /^[A-Za-z0-9][A-Za-z0-9 -]*[A-Za-z0-9]$/;

// Validators return an untranslated message key, or null when the value is
// valid, so the caller decides when to render it.
export const validateFacilityName = (value: string): string | null => {
  // Required only: no minimum length or character rule, since the name is free
  // text the coach chose and it drives the public landing page copy.
  if (!value.trim()) {
    return "Please enter a facility name";
  }
  return null;
};

// Required (product call, 2026-08-26 — the Figma marks only Facility Name and
// Country with asterisks, but filled-check behavior was explicitly kept). No
// structural rule on the value: street formats are wildly inconsistent
// worldwide (Japanese block numbering, Irish house names with no number at
// all), so any rule — "must contain a digit", a minimum length — would reject
// real addresses.
export const validateAddress = (value: string): string | null => {
  if (!value.trim()) {
    return "Please enter a street address";
  }
  return null;
};

// Only the US step shows a State field, so every other country is trivially
// valid — this keeps the country switch from leaving an unsatisfiable error.
export const validateState = (
  value: string,
  country: string,
): string | null => {
  if (country !== "US") {
    return null;
  }
  if (!value) {
    return "Please select a state";
  }
  return null;
};

export const validateCity = (value: string): string | null => {
  const city = value.trim();

  if (!city) {
    return "Please enter a city";
  }
  if (city.length < CITY_MIN_LENGTH) {
    return "Please enter a valid city";
  }
  // No character whitelist: city names are free-form worldwide (CJK, Cyrillic,
  // apostrophes, accents), and any Latin-only pattern would reject real ones.
  return null;
};

// Required everywhere, but only the US is format-checked. postal-codes.json is
// imported third-party data of unknown freshness (3 of its 158 patterns are
// mis-anchored) and nothing downstream consumes this value — no tax, shipping or
// geo lookup — so a stale pattern would hard-block a real coach for no gain.
// The US stays strict because it is the default country, 99999[-9999] is stable,
// and the placeholder already teaches it. Messages stay ZIP-worded in every
// country to match the field's label.
export const validatePostalCodeField = (
  value: string,
  country: string,
): string | null => {
  const code = value.trim();

  if (!code) {
    return "Please enter a ZIP code";
  }

  const isValid =
    country === "US"
      ? validatePostalCode(code, "US")
      : LOOSE_POSTAL_CODE.test(code);

  if (!isValid) {
    // Kept terse on purpose: in US mode this renders under a 120px column, so a
    // longer message (with a format example) would wrap to half a dozen lines.
    // The expected format is taught by the field placeholder instead.
    return "Enter a valid ZIP code";
  }
  return null;
};

// Anchored, unlike app/utils/validate's isValidEmail, which has no ^ or $ and so
// accepts any string merely *containing* an address ("a@b.co <script>" passes).
// Its two existing callers depend on that behaviour, so this step carries its own
// rule rather than tightening a shared helper out from under them.
const EMAIL = /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/;

export const validateFacilityEmail = (value: string): string | null => {
  const email = value.trim();

  if (!email) {
    return "Please enter a facility email";
  }
  if (!EMAIL.test(email)) {
    return "Enter a valid email address";
  }
  return null;
};

// react-phone-input-2 keeps the dial code inside its value, so an untouched field
// holds "1" rather than "" — hence the comparison against dialCode instead of an
// emptiness test, which would read the default US code as a filled-in number.
export const validateFacilityPhone = (
  value: string,
  dialCode: string,
): string | null => {
  const digits = value.replace(/\D/g, "");

  if (!digits || digits === dialCode) {
    return "Please enter a phone number";
  }

  // isPossiblePhoneNumber (length) rather than isValidPhoneNumber (allocated
  // prefix), for the same reason the postal code above is only format-checked in
  // the US: libphonenumber-js ships its metadata table in the bundle, so a coach
  // whose prefix was allocated after this version would be hard-blocked for no
  // gain. Length still catches the typo this field actually sees — a digit
  // dropped or doubled — in every country.
  if (!isPossiblePhoneNumber(`+${digits}`)) {
    return "Enter a valid phone number";
  }
  return null;
};

// Required only, with no character whitelist and no minimum length — the same
// reasoning as Address and Facility Name above. Real names carry apostrophes,
// hyphens, spaces and non-Latin scripts, and mononyms exist, so any structural
// rule ("letters only", "at least two characters") would reject real coaches.
export const validateFirstName = (value: string): string | null => {
  if (!value.trim()) {
    return "Please enter your first name";
  }
  return null;
};

export const validateLastName = (value: string): string | null => {
  if (!value.trim()) {
    return "Please enter your last name";
  }
  return null;
};

// Required only, like Facility Name: the session name is free text the coach chose
// and athletes read it when booking.
export const validateSessionName = (value: string): string | null => {
  if (!value.trim()) {
    return "Please enter a session name";
  }
  return null;
};

// Digits with an optional 2-decimal cents part. Zero passes, so a free intro
// session is expressible; negatives, letters and "49.999" do not. The field's "$"
// is a placeholder, not expected input, so the value stays a machine-usable
// number for checkout later rather than free text like "Contact for pricing".
const RATE = /^\d+(\.\d{1,2})?$/;

export const validateRate = (value: string): string | null => {
  const rate = value.trim();

  if (!rate) {
    return "Please enter a rate";
  }
  if (!RATE.test(rate)) {
    return "Enter a valid rate";
  }
  return null;
};
